import React from 'react';
import { useParams } from 'react-router-dom';
import { ArcPolyline, DestinationMarker, TakeOffMarker } from '@src/core/controllers';
import { EnRouteMapProps } from './types';
import { RouteWaypointLabel } from '@src/features/order/pages/upsert-mission-page/components';
import DroneHomePath from '@src/features/order/pages/upsert-mission-page/components/drone-home-path';
import { splitHomeLeg } from '@src/features/order/pages/upsert-mission-page/utils';
import { useGetOrderMissionQuery } from '@src/features/order/redux';
import { useFlightProvider } from '@src/features/order/provider';
import { useCameraLayout } from '@src/features/order/components/camera-view/camera-layout-context';
import FlightDrone from '@src/features/drone/components/flight-drone';
import { getFlightPosition } from '@src/features/order/provider/flight-provider/flight-position-cache';
import { GoHereLayer } from './go-here-layer';
import { resolveFallbackDronePosition } from './resolve-fallback-position';

const EnRouteMap: React.FC<EnRouteMapProps> = (props) => {
  const {
    viewer,
    points = [],
    resetGoHere,
    missionId: propMissionId,
    rotation,
    fallbackDroneSerial,
    fallbackPosition,
  } = props;
  const { missionId: urlMissionId } = useParams();
  const missionId = propMissionId ?? urlMissionId;

  const { data } = useGetOrderMissionQuery(missionId, {
    skip: !missionId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const { flightData } = useFlightProvider();
  const { setCameraSource } = useCameraLayout();

  const serialNumber = data?.drone?.serialNumber;
  const telemetrySerial = flightData?.serialNumber as string | undefined;

  React.useEffect(() => {
    const sn = serialNumber ?? telemetrySerial;
    if (sn) {
      setCameraSource({ serialNumber: sn, isViewer: viewer, resetGoHere, isEnRoute: true });
    } else if (!missionId) {
      setCameraSource(null);
    }
  }, [serialNumber, telemetrySerial, missionId, viewer, resetGoHere]);

  const dronePosition = React.useMemo<google.maps.LatLngLiteral | null>(() => {
    if (!serialNumber) return null;
    const cached = getFlightPosition(serialNumber);
    if (cached) {
      const lat = Number(cached.latitude);
      const lng = Number(cached.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    }
    if (data?.drone) {
      const lat = Number(data.drone.latitude);
      const lng = Number(data.drone.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    }
    return null;
  }, [serialNumber, flightData, data?.drone]);

  if (!missionId) return null;

  if (!data) {
    const origin = resolveFallbackDronePosition({
      fallbackDroneSerial,
      fallbackPosition,
      flightData,
    });
    if (!origin && points.length === 0) return null;

    return (
      <React.Fragment>
        {origin && (
          <FlightDrone
            drone={{ latitude: origin.lat, longitude: origin.lng }}
            rotation={rotation}
          />
        )}
        <GoHereLayer points={points} origin={origin} />
      </React.Fragment>
    );
  }

  const { homePoint, routeWaypoints } = splitHomeLeg(data.waypoints ?? []);

  const path = routeWaypoints
    .map((wp) => ({ lat: Number(wp.latitude), lng: Number(wp.longitude) }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));

  const takeoffPoint = routeWaypoints.find((wp) => !wp.orderId) ?? routeWaypoints[0];
  const startLat = Number(takeoffPoint?.latitude);
  const startLng = Number(takeoffPoint?.longitude);
  const hasValidStart = Number.isFinite(startLat) && Number.isFinite(startLng);

  const homeLat = Number(homePoint?.latitude);
  const homeLng = Number(homePoint?.longitude);
  const hasHome = Number.isFinite(homeLat) && Number.isFinite(homeLng);

  const coordKey = (wp: { latitude: string | number; longitude: string | number }): string =>
    `${Number(wp.latitude).toFixed(6)},${Number(wp.longitude).toFixed(6)}`;
  const takeoffKey = takeoffPoint ? coordKey(takeoffPoint) : null;
  const seenWaypointKeys = new Set<string>();
  const numberedWaypoints: { wp: (typeof routeWaypoints)[number]; waypointNumber: number }[] = [];
  routeWaypoints.forEach((wp) => {
    if (wp.orderId) return;
    const key = coordKey(wp);
    if (key === takeoffKey || seenWaypointKeys.has(key)) return;
    seenWaypointKeys.add(key);
    numberedWaypoints.push({ wp, waypointNumber: numberedWaypoints.length + 1 });
  });

  const errorOrigin =
    dronePosition ??
    (Number.isFinite(Number(data.drone?.latitude)) && Number.isFinite(Number(data.drone?.longitude))
      ? { lat: Number(data.drone.latitude), lng: Number(data.drone.longitude) }
      : null);

  return (
    <React.Fragment>
      {path && path.length >= 2 && <ArcPolyline.future path={path} />}

      <FlightDrone
        drone={
          data.drone
            ? {
              ...data.drone,
              latitude: dronePosition?.lat ?? Number(data.drone.latitude),
              longitude: dronePosition?.lng ?? Number(data.drone.longitude),
            }
            : undefined
        }
        rotation={rotation}
      />

      {data.orders?.map((order) => {
        const lat = Number(order.deliveryLocation.latitude);
        const lng = Number(order.deliveryLocation.longitude);
        if (isNaN(lat) || isNaN(lng)) return null;

        return (
          <DestinationMarker.pending key={order.id} position={{ lat, lng }} />
        );
      })}

      {hasValidStart && (
        <TakeOffMarker.default position={{ lat: startLat, lng: startLng }} />
      )}

      {hasHome && hasValidStart && (
        <DroneHomePath
          from={{ lat: homeLat, lng: homeLng }}
          to={{ lat: startLat, lng: startLng }}
        />
      )}

      {numberedWaypoints.map(({ wp, waypointNumber }) => {
        const lat = Number(wp.latitude);
        const lng = Number(wp.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return (
          <RouteWaypointLabel
            key={`wp-${waypointNumber}`}
            waypointNumber={waypointNumber}
            position={{ lat, lng }}
          />
        );
      })}

      <GoHereLayer points={points} origin={errorOrigin} />
    </React.Fragment>
  );
};

export default EnRouteMap;
